// OWNER: FRONTEND-ADMIN. Pure audio helpers for voicemail-drop recordings (SPEC §15.8):
// mono mixdown, band-limited resampling, silence trim, peak normalisation and a 16-bit PCM WAV (RIFF) encoder.
// No DOM types on purpose: the worker tsconfig compiles tests/admin-wav.test.ts, which imports this file.

export const DROP_SAMPLE_RATE = 8000;       // Twilio <Play> and the PSTN are 8 kHz; smaller upload, no transcode surprises
export const MAX_DROP_SECONDS = 30;         // SPEC §15.8 hard limit
export const WAV_HEADER_BYTES = 44;

/** Average N equal-length channels into one. A single channel is copied. */
export function mixToMono(channels: Float32Array[]): Float32Array {
  if (channels.length === 0) return new Float32Array(0);
  const n = channels[0].length;
  for (const ch of channels) if (ch.length !== n) throw new RangeError('Channels must have the same length.');
  if (channels.length === 1) return channels[0].slice();
  const out = new Float32Array(n);
  const k = 1 / channels.length;
  for (const ch of channels) for (let i = 0; i < n; i++) out[i] += ch[i] * k;
  return out;
}

/** Number of output samples resample() produces for an input length. */
export function resampledLength(inputLength: number, fromRate: number, toRate: number): number {
  return Math.floor((inputLength * toRate) / fromRate);
}

function sinc(x: number): number {
  if (x === 0) return 1;
  const px = Math.PI * x;
  return Math.sin(px) / px;
}

/**
 * Windowed-sinc (Blackman) resampler. When downsampling, the kernel's cutoff sits just under the new Nyquist
 * frequency so speech above 4 kHz is removed instead of folding back as aliasing. `zeros` = sinc lobes per side.
 */
export function resample(input: Float32Array, fromRate: number, toRate: number, zeros = 12): Float32Array {
  if (!(fromRate > 0) || !(toRate > 0)) throw new RangeError('Sample rates must be positive.');
  if (fromRate === toRate) return input.slice();
  const outLen = resampledLength(input.length, fromRate, toRate);
  const out = new Float32Array(outLen);
  const step = fromRate / toRate;                       // input samples per output sample
  const cutoff = Math.min(1, toRate / fromRate) * 0.94; // fraction of the input Nyquist
  const half = Math.ceil(zeros / cutoff);               // kernel half-width in input samples
  // Tabulate the kernel once (RES points per input sample, linear interpolation between): ~10x faster than
  // evaluating sin/cos per tap, which matters for 30 s of 48 kHz audio on a setter's laptop.
  const RES = 256;
  const tableLen = half * RES + 2;
  const table = new Float32Array(tableLen);
  for (let t = 0; t < tableLen; t++) {
    const x = t / RES;
    if (x >= half) { table[t] = 0; continue; }
    const u = x / half;
    const win = 0.42 + 0.5 * Math.cos(Math.PI * u) + 0.08 * Math.cos(2 * Math.PI * u);
    table[t] = cutoff * sinc(cutoff * x) * win;
  }
  const last = input.length - 1;
  for (let i = 0; i < outLen; i++) {
    const center = i * step;
    const j0 = Math.max(0, Math.ceil(center - half));
    const j1 = Math.min(last, Math.floor(center + half));
    let acc = 0;
    let wsum = 0;
    for (let j = j0; j <= j1; j++) {
      const pos = Math.abs(j - center) * RES;           // kernel is symmetric
      const k = pos | 0;
      const f = pos - k;
      const w = table[k] + (table[k + 1] - table[k]) * f;
      acc += input[j] * w;
      wsum += w;
    }
    out[i] = wsum !== 0 ? acc / wsum : 0;
  }
  return out;
}

/**
 * Trim leading/trailing silence. A sample is "sound" when |x| >= threshold. Keeps `padSec` of room on each side
 * so the first syllable isn't clipped. Returns the input unchanged when nothing crosses the threshold.
 */
export function trimSilence(samples: Float32Array, sampleRate: number, threshold = 0.02, padSec = 0.15): Float32Array {
  let first = -1;
  let lastIdx = -1;
  for (let i = 0; i < samples.length; i++) {
    if (Math.abs(samples[i]) >= threshold) { if (first < 0) first = i; lastIdx = i; }
  }
  if (first < 0) return samples.slice();
  const pad = Math.round(padSec * sampleRate);
  return samples.slice(Math.max(0, first - pad), Math.min(samples.length, lastIdx + pad + 1));
}

export function peak(samples: Float32Array): number {
  let p = 0;
  for (let i = 0; i < samples.length; i++) { const a = Math.abs(samples[i]); if (a > p) p = a; }
  return p;
}

/** Scale so the loudest sample reaches `target` (default ≈ -1 dBFS), never boosting more than `maxGain`. */
export function normalizePeak(samples: Float32Array, target = 0.89, maxGain = 4): { samples: Float32Array; gain: number } {
  const p = peak(samples);
  if (p === 0) return { samples: samples.slice(), gain: 1 };
  const gain = Math.min(maxGain, target / p);
  const out = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i++) out[i] = samples[i] * gain;
  return { samples: out, gain };
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
}

/** Float samples (-1..1, clamped) → 16-bit little-endian PCM mono WAV file bytes. */
export function encodeWav(samples: Float32Array, sampleRate: number): Uint8Array {
  if (!Number.isInteger(sampleRate) || sampleRate <= 0) throw new RangeError('Sample rate must be a positive integer.');
  const dataBytes = samples.length * 2;
  const buf = new ArrayBuffer(WAV_HEADER_BYTES + dataBytes);
  const view = new DataView(buf);
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);    // RIFF chunk size = file size - 8
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);               // fmt chunk size for PCM
  view.setUint16(20, 1, true);                // audio format 1 = PCM
  view.setUint16(22, 1, true);                // channels
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);   // byte rate = rate * channels * bytesPerSample
  view.setUint16(32, 2, true);                // block align
  view.setUint16(34, 16, true);               // bits per sample
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataBytes, true);
  let o = WAV_HEADER_BYTES;
  for (let i = 0; i < samples.length; i++, o += 2) {
    const s = Math.max(-1, Math.min(1, Number.isFinite(samples[i]) ? samples[i] : 0));
    view.setInt16(o, s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff), true);
  }
  return new Uint8Array(buf);
}

export interface WavInfo {
  audioFormat: number;
  channels: number;
  sampleRate: number;
  byteRate: number;
  blockAlign: number;
  bitsPerSample: number;
  dataBytes: number;
  sampleCount: number;       // per channel
  durationSec: number;
}

/** Reads a canonical 44-byte PCM header (the format encodeWav writes). Throws on anything else. */
export function readWavHeader(bytes: Uint8Array): WavInfo {
  if (bytes.length < WAV_HEADER_BYTES) throw new Error('Not a WAV file (too short).');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tag = (o: number) => String.fromCharCode(bytes[o], bytes[o + 1], bytes[o + 2], bytes[o + 3]);
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE' || tag(12) !== 'fmt ' || tag(36) !== 'data') throw new Error('Not a canonical PCM WAV file.');
  const channels = view.getUint16(22, true);
  const sampleRate = view.getUint32(24, true);
  const bitsPerSample = view.getUint16(34, true);
  const dataBytes = view.getUint32(40, true);
  const frameBytes = channels * (bitsPerSample / 8);
  const sampleCount = frameBytes > 0 ? Math.floor(dataBytes / frameBytes) : 0;
  return {
    audioFormat: view.getUint16(20, true),
    channels,
    sampleRate,
    byteRate: view.getUint32(28, true),
    blockAlign: view.getUint16(32, true),
    bitsPerSample,
    dataBytes,
    sampleCount,
    durationSec: sampleRate > 0 ? sampleCount / sampleRate : 0,
  };
}

/**
 * The whole post-decode pipeline, pure: mono → resample to 8 kHz → trim silence → normalise → WAV.
 * `channels` are the decoded (and optionally pre-filtered) channels at `sourceRate`.
 */
export function buildDropWav(channels: Float32Array[], sourceRate: number): { bytes: Uint8Array; durationSec: number; gain: number } {
  const mono = mixToMono(channels);
  const at8k = resample(mono, sourceRate, DROP_SAMPLE_RATE);
  const trimmed = trimSilence(at8k, DROP_SAMPLE_RATE);
  const { samples, gain } = normalizePeak(trimmed);
  return { bytes: encodeWav(samples, DROP_SAMPLE_RATE), durationSec: samples.length / DROP_SAMPLE_RATE, gain };
}
